import { Col, Row } from '../../lib/utils';
import { Body3Bold, Body3Regular, CaptionLight, CaptionRegular, Head4 } from '../../lib/font';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCenterCommRecordAPI, getCenterTodayRecordAPI } from '../../api/dashboard';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import DashBoardDonutChart from './DashBoardDonutChart';
import DashboardGifticonSummary from './DashBoardGifticonSummary';

const DashBoardTodayHistory = ({ authorization, authObject, createAlert }: any) => {
    const navigate = useNavigate();

    const [totalClass, setTotalClass] = useState<number>(0);
    const [completedClass, setCompletedClass] = useState<number>(0);
    const [todayCancelClass, setTodayCancelClass] = useState<number>(0);
    const [todayChangeClass, setTodayChangeClass] = useState<number>(0);

    const [activeMemberCnt, setActiveMemberCnt] = useState<number>(0);
    const [allMemberCnt, setAllMemberCnt] = useState<number>(0);
    const [newMemberCnt, setNewMemberCnt] = useState<number>(0);

    const [ptFeedBackCnt, setPtFeedBackCnt] = useState<number>(0);
    const [counselCnt, setCounselCnt] = useState<number>(0);
    const [facilityCnt, setFacilityCnt] = useState<number>(0);
    const [kioskCnt, setKioskCnt] = useState<number>(0);
    const [etcCnt, setEtcCnt] = useState<number>(0);

    const [date] = useState(dayjs());
    const [chartData, setChartData] = useState<any>(null);

    const getCenterTodayRecord = useQuery(
        ['getCenterTodayRecordAPI'],
        async () => await getCenterTodayRecordAPI(authObject?.centerId, dayjs(date).format('YYYY-MM-DD'), authorization),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    const response = res.data;

                    setTotalClass(response.totalScheduleCount);
                    setCompletedClass(response.completeScheduleCount);
                    setTodayCancelClass(response.canceledScheduleCount);
                    setTodayChangeClass(response.changedScheduleCount);

                    setActiveMemberCnt(response.activeMembershipMemberCount);
                    setAllMemberCnt(response.allMemberCount);
                    setNewMemberCnt(response.newMemberCount);

                    const chartData = [
                        { value: response.completeScheduleCount, color: '#3D73DD' },
                        { value: response.changedScheduleCount, color: '#FFD977' },
                        { value: response.canceledScheduleCount, color: '#FF8777' },
                    ];
                    setChartData(chartData);
                }
            },
            onError: (err) => {
                console.log(err);
            },
            enabled: !!authorization && !!!authObject?.testAccount,
            refetchOnWindowFocus: 'always',
            staleTime: 0,
        }
    );

    const getCenterCommRecord = useQuery(
        ['getCenterCommRecord'],
        async () => await getCenterCommRecordAPI(authObject?.adminId, authorization),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    const response = res.data;

                    setPtFeedBackCnt(response.PTFEEDBACKCount);
                    setCounselCnt(response.COUNSELCount);
                    setFacilityCnt(response.FACILITYCount);
                    setKioskCnt(response.KIOSKCount ?? 0);
                    setEtcCnt(response.ETCCount);
                }
            },
            onError: (err) => {
                console.log(err);
            },
            enabled: !!authorization && !!!authObject?.testAccount,
            refetchOnWindowFocus: 'always',
            staleTime: 0,
        }
    );

    const onClickAddMember = () => {
        navigate('/publicMember/register');
    };

    const scheduleCompletionRate = totalClass > 0 ? Math.ceil((completedClass / totalClass) * 100) : 0;
    const communicationTotal = ptFeedBackCnt + counselCnt + facilityCnt + kioskCnt + etcCnt;
    const shouldUseDemo = !!authObject?.testAccount;

    const scheduleStats = [
        {
            label: '전체 수업',
            value: shouldUseDemo ? 100 : totalClass,
            accent: '#CBD5E1',
            bg: '#F8FAFC',
            border: '#E2E8F0',
        },
        {
            label: '완료 수업',
            value: shouldUseDemo ? 57 : completedClass,
            accent: '#3D73DD',
            bg: '#EFF6FF',
            border: '#BFDBFE',
        },
        {
            label: '당일 취소',
            value: shouldUseDemo ? 10 : todayCancelClass,
            accent: '#FF8777',
            bg: '#FFF1F2',
            border: '#FFD5DB',
        },
        {
            label: '당일 변경',
            value: shouldUseDemo ? 5 : todayChangeClass,
            accent: '#F59E0B',
            bg: '#FFFBEB',
            border: '#FDE68A',
        },
    ];

    const communicationStats = [
        { label: 'PT 피드백', value: ptFeedBackCnt, accent: '#2563EB' },
        { label: '상담 문의', value: counselCnt, accent: '#7C3AED' },
        { label: '시설 문의', value: facilityCnt, accent: '#D97706' },
        { label: '키오스크', value: kioskCnt, accent: '#059669' },
        { label: '기타', value: etcCnt, accent: '#64748B' },
    ];

    return (
        <Col style={{ width: 1192, gap: 18 }}>
            <Row
                style={{
                    width: 1192,
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 20,
                    flexWrap: 'wrap',
                }}
            >
                <Col style={{ gap: 10 }}>
                    <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: -1.1, color: '#0F172A', lineHeight: 1.05 }}>
                        센터 운영 대시보드
                    </div>
                    <Body3Regular style={{ color: '#64748B', lineHeight: 1.7 }}>
                        {dayjs(date).format('YYYY년 M월 DD일(ddd)')} 기준으로 수업 진행, 신규 회원, 선물, 소통 상자 현황을 빠르게 확인하세요.
                    </Body3Regular>
                </Col>

                <button
                    onClick={onClickAddMember}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        height: 48,
                        padding: '0 22px',
                        borderRadius: 14,
                        border: 'none',
                        background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                        color: '#FFFFFF',
                        fontSize: 15,
                        fontWeight: 800,
                        boxShadow: '0 14px 28px rgba(37,99,235,0.22)',
                        cursor: 'pointer',
                    }}
                >
                    + 회원 등록하기
                </button>
            </Row>

            <Row style={{ width: 1192, gap: 16, alignItems: 'stretch' }}>
                <Col style={{ width: 824, flexShrink: 0, gap: 16 }}>
                    <div
                        style={{
                            width: '100%',
                            minHeight: 364,
                            padding: '26px 28px 24px',
                            borderRadius: 28,
                            border: '1px solid #DCE7F5',
                            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FBFF 50%, #EDF5FF 100%)',
                            boxShadow: '0 24px 48px rgba(15,23,42,0.06)',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: -60,
                                right: -40,
                                width: 220,
                                height: 220,
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(191,219,254,0.34) 0%, rgba(191,219,254,0) 70%)',
                            }}
                        />
                        <Row style={{ alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                            <Col style={{ gap: 8 }}>
                                <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', letterSpacing: -0.8 }}>
                                    오늘의 수업 현황
                                </div>
                                <Body3Regular style={{ color: '#64748B', lineHeight: 1.6 }}>
                                    일정 진행률과 당일 취소/변경 상태를 바로 확인할 수 있습니다.
                                </Body3Regular>
                            </Col>
                            <Row
                                style={{
                                    width: 'fit-content',
                                    padding: '8px 14px',
                                    borderRadius: 999,
                                    background: '#EFF6FF',
                                    border: '1px solid #BFDBFE',
                                }}
                            >
                                <CaptionRegular style={{ color: '#1D4ED8', fontSize: 12, fontWeight: 700 }}>
                                    진행률 {scheduleCompletionRate}%
                                </CaptionRegular>
                            </Row>
                        </Row>

                        <Row
                            style={{
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginTop: 24,
                                gap: 28,
                                position: 'relative',
                                zIndex: 1,
                            }}
                        >
                            <div
                                style={{
                                    padding: 10,
                                    borderRadius: 36,
                                    background: 'rgba(255,255,255,0.72)',
                                    border: '1px solid rgba(191,219,254,0.94)',
                                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.72)',
                                }}
                            >
                                <DashBoardDonutChart
                                    chartData={chartData}
                                    completedClass={completedClass}
                                    totalClass={totalClass}
                                />
                            </div>

                            <Col style={{ flex: 1, gap: 18 }}>
                                <Row style={{ gap: 12, flexWrap: 'wrap' }}>
                                    {scheduleStats.map((item) => (
                                        <div
                                            key={item.label}
                                            style={{
                                                flex: '1 1 calc(50% - 6px)',
                                                minWidth: 190,
                                                padding: '16px 18px',
                                                borderRadius: 20,
                                                border: `1px solid ${item.border}`,
                                                background: item.bg,
                                                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
                                            }}
                                        >
                                            <Row style={{ alignItems: 'center', gap: 8 }}>
                                                <span
                                                    style={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: 999,
                                                        backgroundColor: item.accent,
                                                        display: 'inline-block',
                                                    }}
                                                />
                                                <Body3Regular style={{ color: '#475569', fontWeight: 700 }}>{item.label}</Body3Regular>
                                            </Row>
                                            <div
                                                style={{
                                                    marginTop: 10,
                                                    fontSize: 28,
                                                    fontWeight: 800,
                                                    lineHeight: 1,
                                                    letterSpacing: -0.8,
                                                    color: '#0F172A',
                                                }}
                                            >
                                                {item.value}
                                                <span style={{ marginLeft: 4, fontSize: 16, color: '#64748B' }}>건</span>
                                            </div>
                                        </div>
                                    ))}
                                </Row>

                                <Row style={{ alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                                    <Col style={{ gap: 6 }}>
                                        <Body3Bold style={{ color: '#0F172A' }}>
                                            오늘 완료된 수업은 {shouldUseDemo ? 57 : completedClass}건입니다.
                                        </Body3Bold>
                                        <CaptionLight style={{ color: '#64748B' }}>
                                            일정 변경과 취소까지 포함한 오늘 운영 흐름을 한 번에 파악할 수 있어요.
                                        </CaptionLight>
                                    </Col>
                                    <button
                                        onClick={() => navigate('/schedule/history')}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: 40,
                                            padding: '0 16px',
                                            borderRadius: 12,
                                            border: '1px solid #BFDBFE',
                                            backgroundColor: '#FFFFFF',
                                            color: '#1D4ED8',
                                            fontSize: 13,
                                            fontWeight: 800,
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        당일 취소/변경 보기
                                    </button>
                                </Row>
                            </Col>
                        </Row>
                    </div>
                    <DashboardGifticonSummary />
                </Col>

                <Col style={{ width: 352, gap: 16 }}>
                    <div
                        style={{
                            minHeight: 174,
                            padding: '22px 22px 20px',
                            borderRadius: 24,
                            border: '1px solid #D7E9F6',
                            background: 'linear-gradient(135deg, #FFFFFF 0%, #F1F8FF 100%)',
                            boxShadow: '0 18px 36px rgba(15,23,42,0.05)',
                        }}
                    >
                        <Row style={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Col style={{ gap: 8 }}>
                                <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', letterSpacing: -0.6 }}>
                                    오늘의 신규 등록 회원
                                </div>
                                <Body3Regular style={{ color: '#64748B' }}>
                                    신규 등록 추이와 센터 회원 규모를 바로 확인하세요.
                                </Body3Regular>
                            </Col>
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 56,
                                    height: 32,
                                    padding: '0 12px',
                                    borderRadius: 999,
                                    background: '#ECFDF5',
                                    color: '#059669',
                                    fontSize: 12,
                                    fontWeight: 800,
                                    border: '1px solid #A7F3D0',
                                }}
                            >
                                신규
                            </span>
                        </Row>

                        <Row style={{ alignItems: 'flex-end', gap: 8, marginTop: 22 }}>
                            <Head4 style={{ color: '#0F172A', lineHeight: 1 }}>{`+ ${newMemberCnt}`}</Head4>
                            <Body3Bold style={{ color: '#64748B', marginBottom: 4 }}>명</Body3Bold>
                        </Row>

                        <Row style={{ gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
                            <div
                                style={{
                                    flex: 1,
                                    minWidth: 132,
                                    padding: '12px 14px',
                                    borderRadius: 16,
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E2E8F0',
                                }}
                            >
                                <CaptionLight style={{ color: '#64748B' }}>총 회원</CaptionLight>
                                <div style={{ marginTop: 6, fontSize: 20, fontWeight: 800, color: '#111827' }}>{allMemberCnt}명</div>
                            </div>
                            <div
                                style={{
                                    flex: 1,
                                    minWidth: 132,
                                    padding: '12px 14px',
                                    borderRadius: 16,
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E2E8F0',
                                }}
                            >
                                <CaptionLight style={{ color: '#64748B' }}>수강중인 회원</CaptionLight>
                                <div style={{ marginTop: 6, fontSize: 20, fontWeight: 800, color: '#111827' }}>{activeMemberCnt}명</div>
                            </div>
                        </Row>

                        <button
                            onClick={() => navigate('/memberList')}
                            style={{
                                marginTop: 16,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 38,
                                padding: '0 16px',
                                borderRadius: 12,
                                border: '1px solid #BFDBFE',
                                backgroundColor: '#FFFFFF',
                                color: '#1D4ED8',
                                fontSize: 13,
                                fontWeight: 800,
                                cursor: 'pointer',
                            }}
                        >
                            회원 리스트 바로가기
                        </button>
                    </div>

                    <div
                        style={{
                            minHeight: 174,
                            padding: '22px 22px 20px',
                            borderRadius: 24,
                            border: '1px solid #E2E8F0',
                            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFF 100%)',
                            boxShadow: '0 18px 36px rgba(15,23,42,0.05)',
                        }}
                    >
                        <Row style={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Col style={{ gap: 8 }}>
                                <Row style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', letterSpacing: -0.6 }}>
                                        소통 상자 소식
                                    </div>
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '3px 10px',
                                            borderRadius: 999,
                                            backgroundColor: '#FEF3C7',
                                            color: '#B45309',
                                            border: '1px solid #FDE68A',
                                            fontSize: 11,
                                            fontWeight: 700,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        ⏳ 답변 대기중
                                    </span>
                                </Row>
                                <Body3Regular style={{ color: '#64748B' }}>
                                    아직 답변하지 않은 회원 소통 항목의 수예요. 처리하면 자동으로 숫자가 줄어듭니다.
                                </Body3Regular>
                            </Col>
                            <span
                                title="답변 대기중인 건수"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 56,
                                    height: 32,
                                    padding: '0 12px',
                                    borderRadius: 999,
                                    background: '#F5F3FF',
                                    color: '#6D28D9',
                                    fontSize: 12,
                                    fontWeight: 800,
                                    border: '1px solid #DDD6FE',
                                }}
                            >
                                {communicationTotal}건
                            </span>
                        </Row>

                        <Row style={{ gap: 8, flexWrap: 'wrap', marginTop: 20 }}>
                            {communicationStats.map((item) => (
                                <div
                                    key={item.label}
                                    style={{
                                        flex: '1 1 calc(50% - 4px)',
                                        minWidth: 145,
                                        padding: '12px 12px',
                                        borderRadius: 16,
                                        backgroundColor: '#FFFFFF',
                                        border: '1px solid #E2E8F0',
                                    }}
                                >
                                    <CaptionLight style={{ color: '#64748B' }}>{item.label}</CaptionLight>
                                    <div style={{ marginTop: 6, fontSize: 18, fontWeight: 800, color: item.value ? item.accent : '#94A3B8' }}>
                                        {item.value}건
                                    </div>
                                </div>
                            ))}
                        </Row>

                        <button
                            onClick={() => navigate('/member/communication')}
                            style={{
                                marginTop: 16,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 38,
                                padding: '0 16px',
                                borderRadius: 12,
                                border: '1px solid #DDD6FE',
                                backgroundColor: '#FFFFFF',
                                color: '#6D28D9',
                                fontSize: 13,
                                fontWeight: 800,
                                cursor: 'pointer',
                            }}
                        >
                            소통상자 바로가기
                        </button>
                    </div>
                </Col>
            </Row>
        </Col>
    );
};

export default DashBoardTodayHistory;
